// apps/share-web/src/live/live-api.ts
//
// Один опрос якоря: `GET /api/live/:id`.
//
// Маршрут ПУБЛИЧНЫЙ ровно как чтение слов (`text-share-api.ts`) — ссылка и есть
// пропуск. Отсюда отсутствие `credentials: 'include'`, которое стоит у каждого
// соседа за логином: просить cookie у музыканта в зале значит просить то, чего
// у него нет, и заодно слать чужую cookie на маршрут, который о владельце
// ссылки не знает ничего.
//
// Модуль тонкий намеренно: он переводит HTTP в состояния и НЕ решает ничего.
// Вся арифметика — в `live-model.ts`, и она тестируется без сети.

import { DEFAULT_STALE_AFTER_MS, type LiveAnchor, type LiveResult } from './live-model.js';

/**
 * Полу-якорь опаснее ошибки: недостающее поле уехало бы в арифметику и увезло
 * зал в NaN молча. Проверяем каждое поле, которым модель пользуется.
 */
function readAnchor(raw: unknown): LiveAnchor | null {
  if (raw == null || typeof raw !== 'object') return null;
  const a = raw as Record<string, unknown>;
  const nums = ['seq', 'songIdx', 'posMs', 'updatedAtMs'] as const;
  for (const k of nums) if (typeof a[k] !== 'number' || !Number.isFinite(a[k])) return null;
  if (typeof a.playing !== 'boolean') return null;
  return {
    seq: a.seq as number,
    songIdx: a.songIdx as number,
    posMs: a.posMs as number,
    playing: a.playing as boolean,
    updatedAtMs: a.updatedAtMs as number,
    // Ревизия, ПОД КОТОРОЙ ЯКОРЬ ПРИНЯТ, — не та же, что ревизия содержимого.
    // Обязательной не делается: старый воркер её не пишет, и отбрасывать такие
    // якоря значило бы остановить ведение во всём зале ради строгости, которая
    // ничего не ловит.
    contentRev: typeof a.contentRev === 'number' && Number.isFinite(a.contentRev)
      ? (a.contentRev as number)
      : undefined,
  };
}

/**
 * Один опрос якоря.
 *
 * `now` — шов часов, тем же приёмом, что часы в модели: утверждение «ответ шёл
 * 200 мс» недоказуемо, если 200 мс надо переждать. Абсолютных показаний
 * локальных часов наружу не уезжает ни одного — только ИЗМЕРЕННАЯ длина ответа
 * (`halfRttMs`).
 */
export async function fetchLiveAnchor(
  textShareId: string,
  apiBase = '',
  fetchImpl: typeof fetch = fetch,
  now: () => number = () => Date.now(),
): Promise<LiveResult> {
  try {
    const t0 = now();
    const res = await fetchImpl(`${apiBase}/api/live/${encodeURIComponent(textShareId)}`);
    if (res.status === 410) return { state: 'ended' };
    if (res.status === 404) return { state: 'not-found' };
    if (!res.ok) return { state: 'error' };

    const body: unknown = await res.json();
    if (body == null || typeof body !== 'object') return { state: 'error' };
    const b = body as Record<string, unknown>;

    // `anchor: null` — это ОТВЕТ, а не пустота: лайв на этой ссылке ещё не
    // начинался. Сервер отдаёт его двумястами именно для того, чтобы страница
    // отличала «ещё не начался» от «ссылки нет».
    // Ревизия читается ДО ветки якоря: ответ без якоря несёт её тоже (спека
    // §4.2), и это не деталь — «просто читаю слова, лайв не начат» самый частый
    // случай чтения со стойки, и именно он остался бы без обновления.
    const contentRev = typeof b.contentRev === 'number' && Number.isFinite(b.contentRev)
      ? b.contentRev
      : undefined;

    if (b.anchor === null || b.anchor === undefined) return { state: 'no-session', contentRev };

    const anchor = readAnchor(b.anchor);
    if (!anchor) return { state: 'error' };
    if (typeof b.serverNowMs !== 'number' || !Number.isFinite(b.serverNowMs)) return { state: 'error' };

    return {
      state: 'anchor',
      anchor,
      contentRev,
      serverNowMs: b.serverNowMs,
      // ПОЛОВИНА ВРЕМЕНИ ОБХОДА — постоянная часть отставания, и она снимается
      // здесь, потому что измерить её можно только здесь.
      //
      // `serverNowMs` сервер поставил в момент ОТПРАВКИ, а телефон видит его
      // после того, как ответ дошёл и разобрался. Считать, что серверная метка
      // соответствует моменту разбора, значит систематически отставать примерно
      // на половину обхода: локально это мелочь, по мобильной сети 25–100 мс.
      // Обратная половина (запрос в пути) сервера ещё не касалась, поэтому
      // делится именно надвое.
      //
      // Величина ИЗМЕРЕННАЯ, а не константа: постоянная поправка, приложенная к
      // переменной задержке, врала бы ровно тогда, когда сеть ведёт себя не как
      // в замере. И правило 1 модели («часы телефона в возрасте якоря не
      // участвуют») этим не нарушено: наружу уезжает ДЛИТЕЛЬНОСТЬ, снятая одними
      // часами, а разность двух показаний одних часов от их смещения не зависит —
      // тот же аргумент, которым модель считает дельту между опросами.
      //
      // Отрицательного не бывает у длительности: системные часы умеют прыгать
      // назад посреди запроса, и такой прыжок не повод объявить якорь моложе,
      // чем он есть.
      halfRttMs: Math.max(0, (now() - t0) / 2),
      // Порог — серверное решение (2.5 × heartbeat), но его отсутствие не
      // повод считать якорь вечным: без него телефон уезжал бы вперёд
      // навсегда, ровно как в спайке.
      staleAfterMs: typeof b.staleAfterMs === 'number' && b.staleAfterMs > 0
        ? b.staleAfterMs
        : DEFAULT_STALE_AFTER_MS,
    };
  } catch {
    return { state: 'error' };
  }
}
